import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Tabs,
  Tab,
  Divider,
  Snackbar,
  Alert,
  AlertColor,
} from '@mui/material';
import BackLink from '../../BackLink';
import TabPanel from '../../TabPanel';
import EntityTitle from '../../EntityTitle';
import EmojiTransportationIcon from '@mui/icons-material/EmojiTransportation';
import * as providerSlice from '../../../data/provider.slice';
import Entity, { entityClasses } from '../Entity';
import { useLoggedInProviderId } from '../../../hooks/useLoggedInProviderId';
import { useCanViewNotes } from '../../../hooks/permissions/useCanViewNotes';
import { useCanEditProvider } from '../../../hooks/permissions/useCanEditProvider';
import ProviderServicePricing from './ProviderServicePricing';
import Tier3ContactInfo from './Tier3ContactInfo';
import { Tier3Services } from './Tier3Services';
import Tier3ProviderDialog from './Tier3ProviderDialog';
import { Tier3Provider as Tier3ProviderModel } from '../../../data/models/provider.model';

export default function Tier3Provider() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  // const readOnly = useIsReadOnlyUser();
  const canEditProvider = useCanEditProvider(parseInt(params.id!, 10));
  const providerId = useLoggedInProviderId();

  const [editorOpen, setEditorOpen] = React.useState(false);
  const [logoUrl, setLogoUrl] = React.useState<string>();
  const [redirecting, setRedirecting] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState<string>();
  const [snackbarSeverity, setSnackbarSeverity] =
    React.useState<AlertColor>('error');

  const loading = useAppSelector(providerSlice.selectLoadingProvider);
  const loadingError = useAppSelector(providerSlice.selectLoadingProviderError);
  const provider = useAppSelector(providerSlice.selectProvider);
  const uploadingDocument = useAppSelector(
    providerSlice.selectUploadingDocument,
  );
  // const tabOneIndex = useAppSelector(providerSlice.selectTabsetOneIndex);
  const [tabTwoIndex, setTabTwoIndex] = React.useState(0);
  const savingSuccess = useAppSelector(providerSlice.selectSavingSuccess);

  React.useEffect(() => {
    const id = parseInt(params.id!, 10);
    dispatch(providerSlice.getProviderAsync(id));
  }, [params, providerId, dispatch]);

  React.useEffect(() => {
    const id = parseInt(params.id || '0', 10);
    if (provider?.transportationProviderID === id) {
      if (provider?.transportationProviderTierID !== 3) {
        navigate(`/providers/${id}`);
      }
    }
  }, [params, provider]);

  // React.useEffect(() => {
  //   if (providerId) {
  //     const settings = provider?.Settings || [];
  //     const setting = settings.find(
  //       (s: any) => s.SettingOption.ProviderSettingOptionBC === 'LOGOURL',
  //     );
  //     if (setting) {
  //       setLogoUrl(setting.SettingValue);
  //     }
  //   }
  // }, [providerId, provider]);

  React.useEffect(() => {
    if (providerId) {
      setSnackbarMessage(savingSuccess || undefined);
      setSnackbarSeverity('success');
    }
  }, [savingSuccess, providerId]);

  React.useEffect(() => {
    setSnackbarMessage(loadingError || undefined);
    setSnackbarSeverity('error');
  }, [loadingError]);

  const handleTopTabChange = (
    event: React.ChangeEvent<{}>,
    newValue: number,
  ) => {
    dispatch(providerSlice.setTabsetOneIndex(newValue));
  };

  const handleBottomTabChange = (
    event: React.ChangeEvent<{}>,
    newValue: number,
  ) => {
    setTabTwoIndex(newValue);
  };

  const editProvider = () => {
    setEditorOpen(true);
  };

  const closeProviderEditor = () => {
    setEditorOpen(false);
    dispatch(providerSlice.resetSavingFlags());
  };

  const onSnackbarClose = () => {
    setSnackbarMessage(undefined);
    dispatch(providerSlice.setSavingSuccess(null));
  };

  // This is temporary until we handle the provider security on the server.
  if (redirecting) {
    return null;
  }

  return (
    <Entity>
      {!providerId && <BackLink />}
      <Box component="div" className={entityClasses.content}>
        <Box component="div" className={entityClasses.left}>
          <Card className={entityClasses.card}>
            <CardHeader
              title={
                <EntityTitle
                  icon={<EmojiTransportationIcon fontSize="large" />}
                  label={provider?.transportationProviderName}
                  loading={loading}
                  readOnly={!canEditProvider}
                  logo={logoUrl}
                  onEdit={editProvider}
                />
              }
            />
            <Divider />
            {!loading && (
              <>
                <CardContent>
                  <Tier3ContactInfo
                    provider={provider as Tier3ProviderModel}
                    titleStyle={entityClasses.section}
                    isInternalUser={!providerId}
                  />
                </CardContent>
                <Divider />
                <CardContent>
                  <Tier3Services
                    provider={provider as Tier3ProviderModel}
                    titleStyle={entityClasses.section}
                    isInternalUser={!providerId}
                  />
                </CardContent>
              </>
            )}
          </Card>
        </Box>
        {/* <Box component="div" className={entityClasses.left}>
          <Card className={entityClasses.card}>
            <CardHeader
              title={
                <EntityTitle
                  icon={<EmojiTransportationIcon fontSize="large" />}
                  label={provider?.TransportationProviderName}
                  loading={loading}
                  readOnly={!canEditProvider}
                  logo={logoUrl}
                  onEdit={editProvider}
                />
              }
            />
            <Divider />
            {!loading && (
              <CardContent>
                <ContactInfo
                  provider={provider}
                  titleStyle={entityClasses.section}
                />
              </CardContent>
            )}
          </Card>

          {!loading && (
            <Card className={entityClasses.card}>
              <CardContent>
                <BillingInfo
                  provider={provider}
                  titleStyle={entityClasses.section}
                />
              </CardContent>
            </Card>
          )}

          {!loading && (
            <Card className={entityClasses.card}>
              <CardContent>
                <LegalInfo
                  provider={provider}
                  titleStyle={entityClasses.section}
                />
              </CardContent>
            </Card>
          )}
        </Box> */}
        {!loading && (
          <Box component="div" className={entityClasses.right}>
            {/* <Box component="div">
              <Paper className={entityClasses.tabs}>
                <Tabs
                  indicatorColor="primary"
                  textColor="primary"
                  value={tabOneIndex}
                  onChange={handleTopTabChange}
                  variant="fullWidth">
                  <Tab label={t('provider.info')} />
                  <Tab label={t('common.documents')} />
                  <Tab label={t('common.insurance')} />
                  <Tab label={t('preferences.title')} />
                </Tabs>
                <TabPanel value={tabOneIndex} index={0}>
                  <TransportationInfo
                    provider={provider}
                    isInternalUser={!providerId}
                  />
                </TabPanel>
                <TabPanel value={tabOneIndex} index={1}>
                  <ProviderDocuments
                    provider={provider}
                    uploadingDocument={uploadingDocument}
                    readOnly={!canEditProvider}
                    allowView={!providerId}
                  />
                </TabPanel>
                <TabPanel value={tabOneIndex} index={2}>
                  <InsuranceInfo provider={provider} />
                </TabPanel>
                <TabPanel value={tabOneIndex} index={3}>
                  <TripPreferences
                    providerId={provider?.TransportationProviderID}
                  />
                </TabPanel>
              </Paper>
            </Box> */}

            <Box component="div">
              <Paper className={entityClasses.tabs}>
                <Tabs
                  indicatorColor="primary"
                  textColor="primary"
                  value={tabTwoIndex}
                  onChange={handleBottomTabChange}
                  variant="fullWidth">
                  {/* <Tab label={t('common.drivers')} />
                  <Tab label={t('common.vehicles')} />
                  <Tab label={t('common.users')} /> */}
                  <Tab label={t('provider.servicePricing')} />
                  {/* {canViewNotes ? <Tab label={t('provider.notes')} /> : null} */}
                </Tabs>
                {/* <TabPanel value={tabTwoIndex} index={0}>
                  {provider ? <Drivers provider={provider} /> : <></>}
                </TabPanel>
                <TabPanel value={tabTwoIndex} index={1}>
                  <Vehicles provider={provider} />
                </TabPanel>
                <TabPanel value={tabTwoIndex} index={2}>
                  <Users provider={provider} />
                </TabPanel> */}
                <TabPanel value={tabTwoIndex} index={0}>
                  {provider ? (
                    <ProviderServicePricing provider={provider} />
                  ) : null}
                </TabPanel>
                {/* {canViewNotes ? (
                  <TabPanel value={tabTwoIndex} index={4}>
                    <Notes providerId={parseInt(params.id!, 10)} />
                  </TabPanel>
                ) : null} */}
              </Paper>
            </Box>
          </Box>
        )}
      </Box>
      {provider ? (
        <Tier3ProviderDialog
          title={t('providers.tier3Edit')}
          open={editorOpen}
          provider={provider as Tier3ProviderModel}
          close={closeProviderEditor}
        />
      ) : null}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={onSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Entity>
  );
}
